/**
 * AdminsPageV2 — redesigned Admin users list.
 *
 * Filters out the currently-logged-in admin (parity with v1 — admins
 * can't edit themselves through this page; they manage their own
 * profile on Account).
 *
 * Columns: Avatar+Name / Email / Roles (chip cluster) / Actions
 * (reset password, edit, delete).
 */
import React, { useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import {
  deleteAdmin,
  fetchAllAdmins,
  resetPasswordAdmin,
} from '../../../../actions/adminsActions';
import SessionHelper from '../../../Helper/SessionHelper';
import LoadingBackdrop from '../../../Common/LoadingBackdrop';
import {
  PageHeader,
  Button,
  LibTable,
  IconButton,
  cn,
} from '../../../redesign';
import AdminAvatar from './AdminAvatar';

const PlusSign = (
  <span style={{ fontSize: 16, lineHeight: 1 }} aria-hidden>+</span>
);

const EditIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
  </svg>
);

const TrashIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
);

const KeyIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="8" cy="15" r="4" />
    <path d="m10.85 12.15 8.65-8.65" />
    <path d="m18 5 3 3" />
    <path d="m15 8 3 3" />
  </svg>
);

function RoleChip({ children }) {
  return (
    <span
      className="font-condensed font-bold uppercase shrink-0"
      style={{
        fontSize: 10,
        letterSpacing: '0.08em',
        padding: '2px 7px',
        borderRadius: 999,
        background: '#1A8A9818',
        color: '#1A8A98',
        border: '1px solid #1A8A9833',
      }}
    >
      {children}
    </span>
  );
}

export default function AdminsPageV2() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loading = useSelector((s) => s.admins.loading);
  const admins = useSelector((s) => s.admins.admins);
  const me = SessionHelper.currentUser();

  useEffect(() => {
    dispatch(fetchAllAdmins());
  }, [dispatch]);

  const visible = useMemo(() => {
    if (!Array.isArray(admins)) return [];
    return admins.filter((a) => a.admin_id !== me?.admin_id);
  }, [admins, me?.admin_id]);

  const handleDelete = (id, name) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `Delete admin "${name}"? You won't be able to recover them.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'No, cancel',
    }).then((result) => {
      if (result.value) dispatch(deleteAdmin(id));
    });
  };

  const handleReset = (id, name) => {
    Swal.fire({
      title: 'Reset password?',
      text: `Reset password for "${name}"? This can't be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, reset it!',
      cancelButtonText: 'No, cancel',
    }).then((result) => {
      if (result.value) dispatch(resetPasswordAdmin(id));
    });
  };

  const totalLabel = `${visible.length.toLocaleString()} total`;

  return (
    <div className="bg-bg min-h-full">
      <LoadingBackdrop open={!!loading} />
      <PageHeader
        eyebrow="Settings & Admin"
        title="Admin users"
        total={totalLabel}
        actions={
          <Link to="/admins/create" className="no-underline">
            <Button variant="coral" leadingIcon={PlusSign}>
              Add new admin
            </Button>
          </Link>
        }
      />

      <div style={{ padding: '0 28px 32px' }}>
        {visible.length === 0 ? (
          <div
            className="bg-surface border border-sand text-text-muted italic text-center"
            style={{ borderRadius: 12, padding: '40px 16px', fontSize: 13 }}
          >
            {loading
              ? 'Loading admins…'
              : 'No other admins yet — add one to get started.'}
          </div>
        ) : (
          <LibTable
            fixed
            className="border border-sand"
            style={{ borderRadius: 12, overflow: 'hidden' }}
          >
            <colgroup>
              {/* admin (avatar + name) */}
              <col />
              {/* email */}
              <col style={{ width: '24%' }} />
              {/* roles */}
              <col style={{ width: '28%' }} />
              {/* actions */}
              <col style={{ width: 140 }} />
            </colgroup>
            <LibTable.Head>
              <tr>
                <LibTable.Th>Admin</LibTable.Th>
                <LibTable.Th>Email</LibTable.Th>
                <LibTable.Th>Roles</LibTable.Th>
                <LibTable.Th style={{ textAlign: 'right' }}>Actions</LibTable.Th>
              </tr>
            </LibTable.Head>
            <tbody>
              {visible.map((a) => {
                return (
                  <LibTable.Row key={a.admin_id}>
                    <LibTable.Td>
                      <div className="flex items-center min-w-0" style={{ gap: 12 }}>
                        <AdminAvatar
                          id={a.admin_id}
                          name={a.admin_name}
                          image={a.admin_image}
                          size={36}
                        />
                        <Link
                          to={`/admins/${a.admin_id}/edit`}
                          className="font-semibold text-navy no-underline hover:text-teal-green-dark truncate"
                          style={{ fontSize: 13.5, lineHeight: 1.25 }}
                          title={a.admin_name || ''}
                        >
                          {a.admin_name || 'Untitled'}
                        </Link>
                      </div>
                    </LibTable.Td>
                    <LibTable.Td>
                      <span
                        className="font-mono text-text-muted truncate block"
                        style={{ fontSize: 12 }}
                        title={a.admin_email}
                      >
                        {a.admin_email}
                      </span>
                    </LibTable.Td>
                    <LibTable.Td>
                      <div className="flex flex-wrap" style={{ gap: 4 }}>
                        {Array.isArray(a.roles) && a.roles.length > 0 ? (
                          a.roles.map((r) => (
                            <RoleChip key={r.role_id}>{r.role_name}</RoleChip>
                          ))
                        ) : (
                          <span
                            className="text-text-muted italic"
                            style={{ fontSize: 12 }}
                          >
                            no roles
                          </span>
                        )}
                      </div>
                    </LibTable.Td>
                    <LibTable.Td style={{ textAlign: 'right' }}>
                      <div
                        className={cn(
                          'inline-flex opacity-0 transition-opacity duration-150',
                          'group-hover:opacity-100',
                        )}
                        style={{ gap: 4 }}
                      >
                        <IconButton
                          size="sm"
                          aria-label="Reset password"
                          title="Reset password"
                          onClick={() => handleReset(a.admin_id, a.admin_name)}
                        >
                          {KeyIconSvg}
                        </IconButton>
                        <Link
                          to={`/admins/${a.admin_id}/edit`}
                          aria-label={`Edit ${a.admin_name}`}
                        >
                          <IconButton size="sm" aria-label="Edit">{EditIconSvg}</IconButton>
                        </Link>
                        <IconButton
                          size="sm"
                          aria-label="Delete"
                          title="Delete"
                          onClick={() => handleDelete(a.admin_id, a.admin_name)}
                        >
                          {TrashIconSvg}
                        </IconButton>
                      </div>
                    </LibTable.Td>
                  </LibTable.Row>
                );
              })}
            </tbody>
          </LibTable>
        )}
      </div>
    </div>
  );
}
